import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import apiClient from '../../services/api';

// Use env var if set, otherwise use relative URL (works for same-domain backend)
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // In production (ondigitalocean.app or services.knls.in), use relative URL
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '/api';
  }
  // Development fallback
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const qualifications = [
  'SSC',
  'Intermediate',
  'ITI',
  'Degree',
  'B.Tech',
  'M.Tech',
  'B.Com',
  'M.Com',
  'B.Sc',
  'M.Sc',
  'Post Graduate',
];

const departments = ['RRB', 'UPPSC', 'Postal', 'Teacher', 'Police', 'Bank', 'SSC', 'Other'];
const casteOptions = ['OC', 'EWS', 'OBC', 'BC-A', 'BC-B', 'BC-C', 'BC-D', 'SC', 'ST'];

const SubscribersManagement = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSubscriber, setEditingSubscriber] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [formData, setFormData] = useState({
    userPhone: '',
    userName: '',
    dateOfBirth: '',
    caste: '',
    qualification: '',
    interestedDepartments: [],
    interestedCategories: [],
    isActive: true,
  });

  const { data, isLoading, error } = useQuery(
    ['job-alert-subscribers', search],
    async () => {
      const response = await apiClient.get(`/jobalerts/subscribers`, {
        params: { search },
      });
      return response.data;
    }
  );

  const createMutation = useMutation(
    async (values) => {
      const response = await apiClient.post(`/jobalerts/subscribers`, values);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Subscriber created successfully');
        queryClient.invalidateQueries('job-alert-subscribers');
        handleCloseDialog();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create subscriber');
      },
    }
  );

  const updateMutation = useMutation(
    async ({ id, ...values }) => {
      const response = await apiClient.put(`/jobalerts/subscribers/${id}`, values);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Subscriber updated successfully');
        queryClient.invalidateQueries('job-alert-subscribers');
        handleCloseDialog();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update subscriber');
      },
    }
  );

  const deleteMutation = useMutation(
    async (id) => {
      const response = await apiClient.delete(`/jobalerts/subscribers/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Subscriber deleted successfully');
        queryClient.invalidateQueries('job-alert-subscribers');
        setDeleteDialog(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete subscriber');
      },
    }
  );

  const handleOpenDialog = (subscriber = null) => {
    if (subscriber) {
      setEditingSubscriber(subscriber);
      setFormData({
        userPhone: subscriber.userPhone || '',
        userName: subscriber.userName || '',
        dateOfBirth: subscriber.dateOfBirth
          ? format(new Date(subscriber.dateOfBirth), 'yyyy-MM-dd')
          : '',
        caste: subscriber.caste || '',
        qualification: subscriber.qualification || '',
        interestedDepartments: subscriber.interestedDepartments || [],
        interestedCategories: subscriber.interestedCategories || [],
        isActive: subscriber.isActive !== undefined ? subscriber.isActive : true,
      });
    } else {
      setEditingSubscriber(null);
      setFormData({
        userPhone: '',
        userName: '',
        dateOfBirth: '',
        caste: '',
        qualification: '',
        interestedDepartments: [],
        interestedCategories: [],
        isActive: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSubscriber(null);
    setFormData({
      userPhone: '',
      userName: '',
      dateOfBirth: '',
      caste: '',
      qualification: '',
      interestedDepartments: [],
      interestedCategories: [],
      isActive: true,
    });
  };

  const handleSubmit = () => {
    if (!formData.userPhone) {
      toast.error('Phone number is required');
      return;
    }

    const submitData = {
      ...formData,
      dateOfBirth: formData.dateOfBirth || null,
    };

    if (editingSubscriber) {
      updateMutation.mutate({ id: editingSubscriber.id, ...submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const subscribers = data?.data || [];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load subscribers. Please try again.</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Job Alert Subscribers
        </Typography>
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => handleOpenDialog()}>
          Add Subscriber
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search by phone, name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Phone</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>DOB</TableCell>
                  <TableCell>Caste</TableCell>
                  <TableCell>Qualification</TableCell>
                  <TableCell>Interested Departments</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subscribers.map((subscriber) => (
                  <TableRow key={subscriber.id} hover>
                    <TableCell>{subscriber.userPhone}</TableCell>
                    <TableCell>{subscriber.userName || 'N/A'}</TableCell>
                    <TableCell>
                      {subscriber.dateOfBirth
                        ? format(new Date(subscriber.dateOfBirth), 'dd/MM/yyyy')
                        : 'N/A'}
                    </TableCell>
                    <TableCell>{subscriber.caste || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip label={subscriber.qualification || 'N/A'} size="small" color="primary" />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {subscriber.interestedDepartments?.map((dept, idx) => (
                          <Chip key={idx} label={dept} size="small" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={subscriber.isActive ? 'Active' : 'Inactive'}
                        color={subscriber.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenDialog(subscriber)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteDialog(subscriber)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {subscribers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No subscribers found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSubscriber ? 'Edit Subscriber' : 'Add New Subscriber'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number *"
                value={formData.userPhone}
                onChange={(e) => setFormData({ ...formData, userPhone: e.target.value })}
                disabled={!!editingSubscriber}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={formData.userName}
                onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                value={formData.dateOfBirth}
                onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Caste</InputLabel>
                  <Select
                    value={formData.caste}
                    label="Caste"
                    onChange={(e) => setFormData({ ...formData, caste: e.target.value })}
                  >
                    <MenuItem value="">None</MenuItem>
                    {casteOptions.map((caste) => (
                      <MenuItem key={caste} value={caste}>
                        {caste}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Qualification</InputLabel>
                <Select
                  value={formData.qualification}
                  label="Qualification"
                  onChange={(e) => setFormData({ ...formData, qualification: e.target.value })}
                >
                  <MenuItem value="">None</MenuItem>
                  {qualifications.map((qual) => (
                    <MenuItem key={qual} value={qual}>
                      {qual}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={departments}
                value={formData.interestedDepartments}
                onChange={(e, newValue) =>
                  setFormData({ ...formData, interestedDepartments: newValue })
                }
                renderInput={(params) => <TextField {...params} label="Interested Departments" />}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} {...getTagProps({ index })} key={option} />
                  ))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={['railway', 'bank', 'teaching', 'police', 'ssc', 'other']}
                value={formData.interestedCategories}
                onChange={(e, newValue) =>
                  setFormData({ ...formData, interestedCategories: newValue })
                }
                renderInput={(params) => <TextField {...params} label="Interested Categories" />}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} {...getTagProps({ index })} key={option} />
                  ))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={createMutation.isLoading || updateMutation.isLoading}
          >
            {createMutation.isLoading || updateMutation.isLoading ? (
              <CircularProgress size={24} />
            ) : editingSubscriber ? (
              'Update'
            ) : (
              'Create'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
        <DialogTitle>Delete Subscriber</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete subscriber "{deleteDialog?.userPhone}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteMutation.mutate(deleteDialog.id)}
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscribersManagement;

